import { useContext, useEffect, useMemo, useRef } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import type { Moment } from 'moment'
import moment from 'moment'
import { FLIGHTS_MAX_LEAD_TIME_DAYS, toBaseFareClass, type FareClassWithReward } from 'constants/flight'
import Pane from 'components/Common/Pane'
import config from 'constants/config'
import useIsFlightsDisabledForGeoState from 'hooks/Flights/useIsFlightsDisabledForGeoState'
import isTimeLimited from 'lib/offer/isTimeLimited'
import OfferTimeLimitedText from '../OfferTimeLimitedText'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import LineGiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import { OFFER_TYPE_HOTEL_SLUG } from 'constants/offer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import { FormattedMessage, useIntl } from 'react-intl'
import useTravelSynonymLabels from 'hooks/useTravelSynonymLabels'
import commonMessages from 'i18n/commonMessages'
import {
  getCheapestSelectedPackageOptionWithDefault,
  arePricesLoading,
  isBNBLAvailable,
  getFromDurationLabel,
  getEffectiveCharterBundleRoute,
  getEffectiveBundledWithFlightsOnly,
  isAvailableForPurchase,
} from 'selectors/offerPage/offerPageSelectors'
import { getMultiRoomPackageTotals, type MultiRoomPackageTotals } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'LuxKit/Button/TextButton'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import TextLink from 'LuxKit/TextLink'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import Divider from 'LuxKit/Divider'
import GeoContext from 'contexts/geoContext'
import { isDepositAvailableOnOffer } from 'lib/payment/depositsUtils'
import SecureWithDepositTagAndTooltip from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndTooltip'
import ReserveForZeroTag from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroTag'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Group from 'components/utils/Group'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import Tooltip from 'LuxKit/Tooltip'
import getGiftingMessage from 'lib/gifting/getGiftingMessage'
import PriceRowFlightPackage from 'LuxKit/PricePoints/PriceRowFlightPackage'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { setCustomerValue } from 'actions/AuthActions'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import LuxPlusOfferVoucherCallout from 'luxPlus/components/LuxPlusOfferVoucherCallout'
import { computeRewardBundlePrice } from 'lib/maldivesCharter/classicRewardPricing'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import { addFlightPrice, feesToObject, priceWithoutPropertyFees } from 'lib/offer/pricing'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import WalledContent from 'components/Common/WalledContent'
import { generateLoyaltyPointsCalculatorHotelOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import ModalContext from 'contexts/ModalContext'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import { getPriceDisplayFunction, preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { trackClientEvent } from 'analytics/analytics'
import { useQsQueryParams } from 'hooks/useQueryParams'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Clickable from 'components/Common/Clickable'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import { CHARTER_FLIGHT_DETAILS } from 'constants/charterFlight'
import { getFeederOriginAirportCode } from 'lib/charterFlights/charterBundleOfferPageUtils'
import { useCharterBundleFallbackPricing } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useCharterBundleFallbackPricing'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'
import { formatGuestsDisplay } from 'lib/string/stringUtils'
import FlashOfferCharterSummary from './FlashOfferCharterSummary'
import RewardBundlePriceDisplay from './RewardBundlePriceDisplay'
import AgentCommissionPanel from 'agentHub/components/AgentCommissionPanel'
import FlashHotelFlightDealMerchandisingSlot from './FlashHotelFlightDealMerchandisingSlot'

const StyledOfferPartnershipLabel = styled(OfferPartnershipLabel)`
  margin-top: ${rem(12)};
`

const SummaryPane = styled(Pane)`
  position: relative;
`

const PackagePrices = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${rem(4)};
  align-items: center;
`

const ChangeDepartureWrapper = styled.div`
  margin-bottom: auto;
  margin-top: ${rem(8)};
  text-align: center;
`

interface Props {
  offer: App.Offer
  flightOrigin?: App.Airport
  flightDestination?: App.Airport
  rooms: Array<OfferPageRoom>
  className?: string
  cheapestSelectedOption: App.OfferPackageOptionView
  onViewPackageOptions: () => void
  calendarsLoading?: boolean
  checkIn?: string
  isBNBLAvailable: boolean
  isSpoofed: boolean
  durationLabel: string
  availableForPurchase?: boolean
  charterBundleRoute?: App.CharterBundleRoute
  fareClass: FareClassWithReward
  priceMode: OfferPagePriceMode
  bundledWithFlightsOnly: boolean
  /** Aggregate hotel totals across all rooms — used so the summary's reward bundle
   *  matches the multi-room footer instead of showing only one room's hotel. */
  multiRoomPackageTotals?: MultiRoomPackageTotals
}

function FlashOfferSummary(props: Props) {
  const {
    className,
    onViewPackageOptions,
    offer,
    flightOrigin,
    cheapestSelectedOption: packageView,
    rooms,
    flightDestination,
    calendarsLoading,
    checkIn,
    isBNBLAvailable,
    isSpoofed,
    durationLabel,
    availableForPurchase,
    charterBundleRoute,
    fareClass,
    priceMode,
    bundledWithFlightsOnly,
    multiRoomPackageTotals,
  } = props

  const intl = useIntl()
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const isFlightsDisabledForGeoState = useIsFlightsDisabledForGeoState()
  const dispatch = useOfferPageDispatch()
  const appDispatch = useAppDispatch()
  const currentRegionHasFlights = useAppSelector(state => state.geo.currentRegionHasFlights)
  const showModal = useContext(ModalContext)

  const charterRoute = charterBundleRoute ? CHARTER_FLIGHT_DETAILS[charterBundleRoute] : undefined

  const handleScheduleCallback = () => {
    showModal(<RequestAgentCallbackModal type="SALES" />)
  }

  const {
    totalFlightPrice: charterFlightPrice,
    regularTotalFlightPrice: charterRegularFlightPrice,
    loyaltyEarnKey,
    rewardAvailable,
    rewardSeatPointsTotal,
    rewardSeatCashCoPayTotal,
  } = useOfferPageFlightPrice()

  // Charter route: use charter flight price directly (not affected by flightCacheDisabled)
  // Non-charter route: use package flight price if cache is enabled
  const flightPrice = charterRoute
    ? charterFlightPrice // Charter: only use charter price (undefined if not available)
    : !offer.flightCacheDisabled
        ? packageView.flightPrice
        : undefined
  const regularFlightPrice = charterRoute
    ? (charterRegularFlightPrice ?? charterFlightPrice)
    : flightPrice

  const {
    package: pkg,
    pricing: hotelPricing,
    memberPricing: hotelMemberPricing,
  } = packageView

  const pricingWithFlights = regularFlightPrice ? addFlightPrice(hotelPricing, regularFlightPrice, !!charterRoute) : undefined
  const memberPricingWithFlights = (hotelMemberPricing && flightPrice) ? addFlightPrice(hotelMemberPricing, flightPrice, !!charterRoute) : undefined

  const showPrices = packageView.capacityValid && packageView.hotelAvailable
  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled && !isFlightsDisabledForGeoState && currentRegionHasFlights
  const hasFlights = !!pricingWithFlights && (!!charterRoute || !offer.flightCacheDisabled)
  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])

  const onToggleFilter = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    dispatch({ type: OfferPageActions.toggleFilter, filter: 'airport' })
  }

  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const departureTooFarInFuture = useMemo(() => checkInMoment && checkInMoment.diff(moment.utc(), 'days') > FLIGHTS_MAX_LEAD_TIME_DAYS, [checkInMoment])
  const hideNoCacheMessaging = !hasFlights && bundledWithFlightsOnly
  const baseFareClass = toBaseFareClass(fareClass)
  const feederOriginAirportCode = getFeederOriginAirportCode(charterBundleRoute, flightOrigin)

  // Get charter flight dates to check if user's checkIn matches available charter dates
  const { departDate: charterDepartDate } = useCharterFlightDates({
    checkInDate: checkIn,
    nights: pkg.duration,
    charterBundleRoute,
    rooms,
    fareClass: baseFareClass,
    feederOriginAirportCode,
  })

  // Check if user's checkIn matches a valid charter flight date
  const isCheckInMatchingCharterDate = !!checkIn && !!charterDepartDate

  // Determine if we should use fallback pricing for charter bundle
  // Use fallback when: no checkIn date selected, OR checkIn doesn't match any charter flight date
  const shouldUseFallbackPricing = !!charterBundleRoute && (!checkIn || !isCheckInMatchingCharterDate)

  // Fallback pricing evaluates all packages at the current duration (no packageUniqueKey)
  // to show the global cheapest "from" price, matching the search page behavior.
  const { fallbackPricing, fallbackMemberPricing, fallbackHasReturnFareFree } = useCharterBundleFallbackPricing({
    offer: offer as App.HotelOffer,
    rooms,
    charterBundleRoute,
    fareClass: baseFareClass,
    nights: pkg.duration,
    disabled: !shouldUseFallbackPricing,
    feederOriginAirportCode,
  })

  let pricingToDisplay: App.PricingWithValue | undefined = hotelPricing
  let memberPricingToDisplay: App.PricingWithValue | undefined = hotelMemberPricing
  let isUsingFallbackPricing = false
  if (bundledWithFlightsOnly) {
    if (!charterRoute && flightsEnabled && !hideNoCacheMessaging && hasFlights) {
      // Non-charter route: use standard flight pricing
      pricingToDisplay = pricingWithFlights
      memberPricingToDisplay = memberPricingWithFlights
    }
    else if (charterRoute) {
      if (shouldUseFallbackPricing) {
        if (fallbackPricing) {
          pricingToDisplay = fallbackPricing
          memberPricingToDisplay = fallbackMemberPricing
          isUsingFallbackPricing = true
        } else {
          // Fallback data still loading — don't show stale context pricing
          pricingToDisplay = undefined
          memberPricingToDisplay = undefined
        }
      } else if (hasFlights) {
        // Valid date selected - show actual flight price
        pricingToDisplay = pricingWithFlights
        memberPricingToDisplay = memberPricingWithFlights
      } else {
        pricingToDisplay = undefined
        memberPricingToDisplay = undefined
      }
    }
    else {
      pricingToDisplay = undefined
      memberPricingToDisplay = undefined
    }
  }

  const fees = pricingToDisplay ? feesToObject(pricingToDisplay.fees) : {}

  const showMemberPrice = !!memberPricingToDisplay && memberPricingToDisplay.price > 0
  const showDiscountPill = !offer?.isDiscountPillHidden && pricingToDisplay && pricingToDisplay.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const showPriceDetails = (fees.taxesAndFees ?? 0) > 0 || (fees.property ?? 0) > 0 || config.agentHub.isEnabled

  const showSecureWithDepositTag = isDepositAvailableOnOffer(offer, currentCurrency, checkInMoment, hotelPricing.price)
  const isReserveForZeroEnabled = config.RESERVE_FOR_ZERO_ENABLED
  const isReserveForZeroTagShown = (isReserveForZeroEnabled || isSpoofed) && isPackageEligibleForZeroReservation(offer, checkInMoment, pkg.roomRate?.cancellationPolicy?.type)
  const isDepositTagShown = showSecureWithDepositTag
  const shouldShowValue = !!pricingToDisplay && pricingToDisplay.value > pricingToDisplay.price && pkg.shouldDisplayValue
  const isIndiaRegion = currentRegionCode === 'IN'
  const isMemberOnlyOffer = offer.luxPlus.access === 'memberOnly'
  const showDisclaimers = (flightsEnabled && !hideNoCacheMessaging) || (isBNBLAvailable && !isMemberOnlyOffer)
  const shouldShowRoomType = !!pkg.roomType?.name

  let saleUnit = pricingToDisplay?.saleUnit ?? offer.saleUnit
  if (bundledWithFlightsOnly) {
    saleUnit = 'package*'
  }

  useEffect(() => {
    appDispatch(setCustomerValue(hotelPricing.price))
  }, [appDispatch, hotelPricing.price])

  const bookByFormatted = useMemo<Moment | undefined>(() => {
    return offer.bookByDate ? moment(offer.bookByDate) : undefined
  }, [offer.bookByDate])

  const travelUntilFormatted = useMemo<Moment | undefined>(() => {
    return offer.travelToDate ? moment(offer.travelToDate) : undefined
  }, [offer.travelToDate])

  const { partnership } = useContext(GeoContext)

  const { travelSynonym } = useTravelSynonymLabels()
  const viewOptionsLabel = intl.formatMessage(
    {
      id: 'JaczHbGlU3Scp_uyLG7u',
      defaultMessage: 'View {travelSynonym} options',
      description: 'Action button label to view travel options on the offer detail page',
    },
    {
      travelSynonym,
    },
  )

  const giftingMessage = useMemo<string | undefined>(() => {
    if (bookByFormatted && travelUntilFormatted) {
      return getGiftingMessage({ bookBy: bookByFormatted, travelUntil: travelUntilFormatted }, intl)
    }
  }, [bookByFormatted, travelUntilFormatted, intl])

  const isRewardSelected = rewardAvailable && rewardSeatPointsTotal != null
  const showRewardPrice = isRewardSelected && rewardSeatCashCoPayTotal != null && pricingToDisplay != null
  const rewardPriceData = useMemo(() => {
    if (!showRewardPrice || !pricingToDisplay || rewardSeatPointsTotal == null || rewardSeatCashCoPayTotal == null) return undefined
    let hotelPrice = pricingToDisplay.price - (charterFlightPrice ?? 0)
    let hotelMemberPrice = memberPricingToDisplay ? memberPricingToDisplay.price - (charterFlightPrice ?? 0) : undefined
    if (rooms.length > 1 && multiRoomPackageTotals) {
      hotelPrice = multiRoomPackageTotals.hotelPrice
      hotelMemberPrice = multiRoomPackageTotals.hotelMemberPrice
    }
    return computeRewardBundlePrice({
      hotelPrice,
      hotelMemberPrice,
      rewardPointsTotal: rewardSeatPointsTotal,
      rewardCashCoPayTotal: rewardSeatCashCoPayTotal,
    })
  }, [showRewardPrice, pricingToDisplay, rooms.length, multiRoomPackageTotals, charterFlightPrice, memberPricingToDisplay, rewardSeatPointsTotal, rewardSeatCashCoPayTotal])
  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView, currentRegionCode, nights: packageView?.package?.duration }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      packageView,
      flightPrice,
      loyaltyEarnKey,
      skip: !bundledWithFlightsOnly || !hasFlights || !flightsEnabled || !!isRewardSelected,
    }),
  ], [offer, packageView, hasFlights, flightsEnabled, isRewardSelected, currentRegionCode, bundledWithFlightsOnly, flightPrice, loyaltyEarnKey])

  const shouldTrackPrice = availableForPurchase && showPrices && !calendarsLoading && !!pricingToDisplay?.price
  const priceTrackedRef = useRef(false)
  const { searchId } = useQsQueryParams<{ searchId?: string }>()
  useEffect(() => {
    if (shouldTrackPrice && pricingToDisplay?.price && searchId && offer?.id && !priceTrackedRef.current) {
      trackClientEvent({
        subject: `searchId:${searchId}_offerId:${offer.id}`,
        action: pricingToDisplay.price.toString(),
        category: 'price_accuracy',
        type: 'operational',
      })
      priceTrackedRef.current = true
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldTrackPrice, pricingToDisplay?.price, offer?.id])

  const convertToDisplay = getPriceDisplayFunction(priceMode, rooms)
  const rewardSaleUnit = priceMode === 'perPerson'
    ? intl.formatMessage({ id: 'pG1LR1q6N1blZg7L8nls', defaultMessage: 'person', description: 'Per-person pricing unit suffix for reward price on offer summary tile' })
    : saleUnit

  const totalGuests = useMemo(() => sumUpOccupancies(rooms), [rooms])
  const guestsDisplayText = useMemo(() => {
    if (!charterRoute || !totalGuests) return undefined
    return formatGuestsDisplay(totalGuests, intl)
  }, [charterRoute, totalGuests, intl])

  const hotelFlightsText = hideNoCacheMessaging
    ? intl.formatMessage({
        id: '9aOR47WQ5ntQxKg3JM6o',
        defaultMessage: 'Hotel + flights packages available',
        description: 'Text showing hotel and flights packages available on the offer detail page',
      })
    : intl.formatMessage({
        id: 'SwMd2eg2iKtiyHGO3Ffu',
        defaultMessage: 'Hotel + flights',
        description: 'Text showing hotel and flights in the flight search or booking flow',
      })

  const addFlightsText = !isIndiaRegion
    ? intl.formatMessage({
        id: '0PdZSaLtyCgHJmQTIlzz',
        defaultMessage: 'Add flights to the hotel package. Select from all major airlines at the next step.',
        description: 'Message to add flights to hotel package in the flight search or booking flow',
      })
    : intl.formatMessage({
        id: 'AnZz8PHuzait9-MnnL1s',
        defaultMessage: 'Add flights to your hotel package from the best available airlines.',
        description: 'Message to add flights to hotel package for India region',
      })

  return (
    <SummaryPane border="light-foreground" borderRadius="XS" shadow="bottom-small" className={className} data-testid="SummaryPane">
      <Pane padding="28 20">
        <VerticalSpacer gap={12}>
          <Group direction="vertical" gap={16}>
            {availableForPurchase && <WalledContent
              enforced={offer.walledGarden}
              wall={
                <BodyText variant="large" weight="bold">
                  {intl.formatMessage(
                    {
                      id: '5HFnkLMdVWSFkvKbYTqA',
                      defaultMessage: '{durationLabel} packages available',
                      description: 'Text showing duration packages available on the offer detail page',
                    },
                    {
                      durationLabel: offer.durationLabel,
                    },
                  )}
                </BodyText>
              }
            >
              {!charterRoute && showPrices &&
                <PackagePrices>
                  {isTimeLimited(offer) && <OfferTimeLimitedText />}
                  {isDepositTagShown && !isSpoofed && <SecureWithDepositTagAndTooltip offerType={offer.type} />}
                  {isReserveForZeroTagShown && <ReserveForZeroTag />}
                  <Group direction="vertical" horizontalAlign="center" gap={8} fullWidth>
                    <Group direction="vertical" horizontalAlign="center">
                      {bundledWithFlightsOnly &&
                        <CaptionTextBlock variant="large" weight="bold" startIcon={<LinePlaneFlyIcon />}>
                          {hotelFlightsText}
                        </CaptionTextBlock>}
                      <Group direction="vertical" horizontalAlign="center" noDisplayWhenEmpty>
                        <LuxPlusOfferVoucherCallout offer={offer} mode="offer" />
                      </Group>
                      {rewardPriceData && <>
                        <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                        <RewardBundlePriceDisplay
                          points={convertToDisplay(rewardPriceData.points)}
                          cash={convertToDisplay(rewardPriceData.memberCash ?? rewardPriceData.cash)}
                          nonMemberCash={rewardPriceData.memberCash !== undefined ? convertToDisplay(rewardPriceData.cash) : undefined}
                          unitLabel={rewardSaleUnit}
                        />
                      </>}
                      {!rewardPriceData && pricingToDisplay && <>
                        <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                        <LuxPlusPriceStack
                          size="L"
                          price={convertToDisplay(pricingToDisplay.price)}
                          memberPrice={memberPricingToDisplay?.price && convertToDisplay(memberPricingToDisplay?.price)}
                          saleUnit={priceMode === 'perPerson' ? 'person' : saleUnit}
                          testid="pricing"
                          horizontalAlign="center"
                          offerConfig={offer.luxPlus}
                        />
                        {bundledWithFlightsOnly && !rooms && <Caption variant="medium" colour="light-foreground-alt">
                          &nbsp;{intl.formatMessage({
                            id: 't4Npb5kUtaDR0U0OaM3K',
                            defaultMessage: 'Two Adults',
                            description: 'Text showing two adults occupancy in the occupancy or guest picker',
                          })}
                        </Caption>}
                        <PriceRowTaxesAndFees
                          endIcon={showPriceDetails && <OfferPriceDetailsRow
                            offerPage
                            offer={offer}
                            duration={pkg.duration}
                            propertyFees={fees.property}
                            taxesAndFees={fees.taxesAndFees}
                            hotelPrice={priceWithoutPropertyFees(hotelPricing)}
                            hotelMemberPrice={hotelMemberPricing ? priceWithoutPropertyFees(hotelMemberPricing) : undefined}
                            showMemberPrice={showMemberPrice}
                            flightPrice={bundledWithFlightsOnly ? flightPrice : undefined}
                            rooms={rooms}
                            extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
                            dueAtPropertyMessage={offer.property?.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
                            trigger="info-icon"
                            cta={
                              <TextButton
                                kind="primary"
                                fit="flex"
                              >
                                {viewOptionsLabel}
                              </TextButton>
                            }
                            onCtaAfterClose={onViewPackageOptions}
                          />}
                        />
                        {showIncludedGuestsExceeded && (
                          <AdditionalGuestsPopup
                            complex={false}
                            description={intl.formatMessage({
                              id: 'hYSia4zuX5AzjB0ppGm3',
                              defaultMessage: '+ Extra guest surcharge',
                              description: 'Descriptive text for extra guest surcharge on the offer detail page',
                            })}
                            modalContent={pkg.roomPolicyDescription}
                          />
                        )}
                        {shouldShowValue && <PriceRowValueDiscountWithCaption
                          data-testid="value-box"
                          size="M"
                          originalValue={convertToDisplay(pricingToDisplay.value)}
                          discountPercentage={showDiscountPill ? pricingToDisplay.discountPercent : undefined}
                        />}
                      </>}
                    </Group>
                    <FlashHotelFlightDealMerchandisingSlot
                      flightOrigin={flightOrigin}
                      flightDestination={flightDestination}
                      disabled={!flightsEnabled || bundledWithFlightsOnly || !!charterRoute}
                      allowCalculatedFallback
                    />
                    <LoyaltyPointsEarn
                      horizontalAlign="center"
                      calculationRequests={pointsEarnCalculationRequests}
                      calculationType="estimate"
                      offer={offer}
                    />
                  </Group>
                  { partnership && partnership.code !== 'velocity' && <StyledOfferPartnershipLabel offer={offer} />}
                  <LoadingIndicator floating backdrop="grey-light" opaque visible={!!calendarsLoading} />
                </PackagePrices>}
              <AgentCommissionPanel
                offerId={offer.id}
                offerTotal={pricingToDisplay?.price ?? 0}
                retailValue={pricingToDisplay?.value}
                packageId={pkg.id}
                memberPrice={memberPricingToDisplay?.price}
              />
              {charterRoute && showPrices && pricingToDisplay &&
                <FlashOfferCharterSummary
                  pricingToDisplay={pricingToDisplay}
                  memberPricingToDisplay={memberPricingToDisplay}
                  totalGuests={totalGuests}
                  isPricePerPerson={priceMode === 'perPerson'}
                  pkg={pkg}
                  offer={offer}
                  shouldShowValue={shouldShowValue}
                  showDiscountPill={!!showDiscountPill}
                  guestsDisplayText={guestsDisplayText}
                  shouldShowRoomType={shouldShowRoomType}
                  isUsingFallbackPricing={isUsingFallbackPricing}
                  pointsEarnCalculationRequests={pointsEarnCalculationRequests}
                  onViewPackageOptions={onViewPackageOptions}
                  calendarsLoading={calendarsLoading}
                  fareClass={fareClass}
                  checkInDate={checkIn}
                  feederOriginAirportCode={feederOriginAirportCode}
                  fallbackHasReturnFareFree={fallbackHasReturnFareFree}
                  hotelPricing={hotelPricing}
                  hotelMemberPricing={hotelMemberPricing}
                  multiRoomPackageTotals={rooms.length > 1 ? multiRoomPackageTotals : undefined}
                />}
              {!charterRoute && <TextButton
                kind="primary"
                data-testid="OfferAvailable"
                onClick={onViewPackageOptions}
                fit="flex"
              >
                {viewOptionsLabel}
              </TextButton>}
              {showDisclaimers && !charterRoute && <Group direction="vertical" gap={8}>
                {flightsEnabled && !hideNoCacheMessaging && <Group direction="vertical" horizontalAlign="center">
                  {!bundledWithFlightsOnly && hasFlights && <PriceRowFlightPackage
                    product="hotel"
                    data-testid="FlightPrice"
                    price={showMemberPrice ? memberPricingWithFlights?.price : pricingWithFlights.price}
                  />}
                  {!hasFlights && flightOrigin?.code === flightDestination?.code &&
                    <ChangeDepartureWrapper>
                      <PriceRowCaption>
                        {intl.formatMessage(
                          {
                            id: 'e543oU2rlSWn9p_jiHgI',
                            defaultMessage: 'No flights available from {airportName}. Please select another airport.',
                            description: 'Message when no flights available from selected airport',
                          },
                          {
                            airportName: flightOrigin.name,
                          },
                        )}
                      </PriceRowCaption>
                      <TextLink onClick={onToggleFilter} data-filter="airport">
                        {intl.formatMessage({
                          id: 'K_KcYid6JxrWDaCbdmcU',
                          defaultMessage: 'Change departure airport',
                          description: 'Hyperlink text to change departure airport on the offer detail page',
                        })}
                      </TextLink>
                    </ChangeDepartureWrapper>}
                  {!hasFlights && flightOrigin?.code !== flightDestination?.code && !departureTooFarInFuture &&
                    <PriceRowCaption>
                      {addFlightsText}
                    </PriceRowCaption>}
                  {!hasFlights && departureTooFarInFuture &&
                    <ChangeDepartureWrapper>
                      <Caption variant="large" colour="light-foreground-alt">
                        {intl.formatMessage({
                          id: 'HDQzkY8wb9hjqFZx34KE',
                          defaultMessage: 'Flights can only be booked up to 11 months in advance.',
                          description: 'Message about flight booking advance limit in the flights flow',
                        })}
                      </Caption>
                    </ChangeDepartureWrapper>}
                  <AtolProtectedTooltip />
                </Group>}
                {isBNBLAvailable && !isMemberOnlyOffer && <CaptionTextBlock
                  variant="medium"
                  startIcon={<LineGiftIcon />}
                  align="center"
                >
                  <FormattedMessage
                    id="3SwBr-fvo9xT8zk-YLQD"
                    defaultMessage="Gifting available <link>Learn more</link>"
                    description="Text indicating gifting is available with learn more link"
                    values={{
                      link: (chunks: React.ReactNode) => <SoftOfferPageWalledGarden>
                        <Tooltip description={giftingMessage}>
                          <TextLink>{chunks}</TextLink>
                        </Tooltip>
                      </SoftOfferPageWalledGarden>,
                    }}
                  />
                </CaptionTextBlock>}
              </Group>}
            </WalledContent>}
          </Group>
          {!availableForPurchase &&
            <>
              <BodyText
                variant="large"
                weight="bold"
                data-testid="OfferSoldOut"
              >
                {intl.formatMessage({
                  id: 'F_B84EaA5wW9h1hSGVFK',
                  defaultMessage: 'This offer has SOLD OUT',
                  description: 'Message when offer is sold out on the offer listing or detail page',
                })}
              </BodyText>
              <TextButton
                kind="primary"
                to={`/${OFFER_TYPE_HOTEL_SLUG}`}
                fit="flex"
              >
                {intl.formatMessage(commonMessages.browseAllHotelOffers)}
              </TextButton>
            </>}
        </VerticalSpacer>
      </Pane>
      {charterRoute &&
        <>
          <Divider kind="secondary" />
          <Pane background="normal-background" padding={20}>
            <VerticalSpacer gap={8}>
              <VerticalSpacer gap={0}>
                <BodyText variant="large" weight="bold" align="center">
                  {intl.formatMessage({
                    id: 't1KFWNlw-24dNesJOyaS',
                    defaultMessage: 'Have questions?',
                    description: 'Section heading for charter questions section on the offer detail page',
                  })}
                </BodyText>
                <BodyText variant="medium" align="center">
                  {intl.formatMessage({
                    id: '1DMOK9-M2E1bUqktC_eY',
                    defaultMessage: 'Talk to a Maldives travel expert today.',
                    description: 'Text to talk to Maldives travel expert on the offer detail page',
                  })}
                </BodyText>
              </VerticalSpacer>
              <VerticalSpacer gap={8}>
                <Clickable
                  href={charterRoute.supportNumber.href}
                  aria-label={intl.formatMessage({
                    id: 'FVDvnZKcCjJH0Sky8tiV',
                    defaultMessage: 'Local Sales & Support contact',
                    description: 'Accessibility aria-label for local sales and support contact',
                  })}
                  rel="nofollow"
                >
                  <HeadingTextBlock variant="heading5" startIcon={<LinePhoneIcon />} align="center">
                    {charterRoute.supportNumber.humanReadable}
                  </HeadingTextBlock>
                </Clickable>
                <BodyText variant="medium" align="center">
                  {intl.formatMessage(
                    {
                      id: '1Q1ouWkF2r-WyH_VLnVv',
                      defaultMessage: 'or <link>schedule a call back</link>',
                      description: 'Text with link to schedule a callback on the offer detail page',
                    },
                    {
                      link: (chunks: React.ReactNode) => (
                        <TextLink
                          key="link"
                          href="#"
                          onClick={(e) => {
                            e.preventDefault()
                            handleScheduleCallback()
                          }}
                          underline
                        >
                          {chunks}
                        </TextLink>
                      ),
                    },
                  )}
                </BodyText>
              </VerticalSpacer>
            </VerticalSpacer>
          </Pane>
        </>}
    </SummaryPane>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  cheapestSelectedOption: getCheapestSelectedPackageOptionWithDefault(appState, state),
  rooms: state.rooms,
  offer: state.offer,
  checkIn: state.checkInDate,
  flightOrigin: state.airport,
  calendarsLoading: arePricesLoading(appState, state),
  isBNBLAvailable: isBNBLAvailable(state),
  isSpoofed: appState.auth.account.isSpoofed,
  durationLabel: getFromDurationLabel(state, appState),
  availableForPurchase: isAvailableForPurchase(state, appState),
  open: !!state.activeFilter,
  charterBundleRoute: getEffectiveCharterBundleRoute(state),
  fareClass: state.fareClass,
  priceMode: state.priceMode,
  bundledWithFlightsOnly: getEffectiveBundledWithFlightsOnly(state),
  multiRoomPackageTotals: state.multiRoomMode === 'individual' && state.rooms.length > 1 ? getMultiRoomPackageTotals(state, appState) : undefined,
}), FlashOfferSummary, preventRenderWithOpenFilter)
